/**
 * ChatbotStoreContext — provides a chatbot STORE INSTANCE to a subtree.
 *
 * This is the "one reusable chat substrate" seam. The main chat keeps reading the
 * global `useChatbotStore` directly; any other surface (an agentic board run, a
 * memo thread) can wrap its message UI in `<ChatbotStoreProvider conversationId=…>`
 * to get an isolated `createChatbotStore()` instance, then render the SAME chat
 * components against it.
 *
 * Byte-identical guarantee for the main chat: `useChatbotStoreContext` /
 * `useChatbotStoreApi` fall back to the global `useChatbotStore` when there is no
 * provider above them. So converting a shared component from `useChatbotStore(sel)`
 * to `useChatbotStoreContext(sel)` is a no-op when that component renders in the main
 * chat (no provider → global store) and correctly scoped when it renders inside a
 * provider (→ the instance store).
 *
 * NOTE: distinct from the existing `ChatbotProvider` in
 * `components/Chatbot/context/ChatbotContext.tsx`, which manages asset/artifact
 * selection — not the store. They compose; they don't collide.
 */
import React, { createContext, useContext, useRef } from 'react';
import { useStore } from 'zustand';
import {
  useChatbotStore,
  createChatbotStore,
  type ChatbotStoreApi,
} from './chatbotStore';
import type { ChatbotStore } from './types';

const ChatbotStoreContext = createContext<ChatbotStoreApi | null>(null);

interface ChatbotStoreProviderProps {
  /**
   * Provide an existing store instance to share it across mounts. If omitted, the
   * provider lazily creates one via `createChatbotStore(conversationId)`, stable for
   * the lifetime of this provider.
   */
  store?: ChatbotStoreApi;
  /** Seed conversationId for a freshly created instance store (ignored if `store` given). */
  conversationId?: string;
  children: React.ReactNode;
}

export const ChatbotStoreProvider: React.FC<ChatbotStoreProviderProps> = ({
  store,
  conversationId,
  children,
}) => {
  // When the caller owns the store (passes `store`), pass it straight through
  // — an outer coordinator (e.g. the main panel swapping chats) has to be able
  // to change the exposed instance across renders. When the caller only asks
  // us to CREATE a store, memoize at first mount (`createChatbotStore` is not
  // idempotent) and keep that instance stable for the lifetime of this mount.
  const created = useRef<ChatbotStoreApi | null>(null);
  if (!store && created.current === null) {
    created.current = createChatbotStore(conversationId);
  }
  const active = store ?? created.current!;
  return (
    <ChatbotStoreContext.Provider value={active}>
      {children}
    </ChatbotStoreContext.Provider>
  );
};

/**
 * The active chatbot store API for imperative access (getState/setState/subscribe).
 * Falls back to the global singleton when used outside a provider, so existing
 * out-of-render call sites keep working unchanged.
 */
export function useChatbotStoreApi(): ChatbotStoreApi {
  // `useChatbotStore` is a bound hook that also carries StoreApi methods, so it is a
  // valid `ChatbotStoreApi` fallback. The `?? value` is on a plain value (not a hook
  // call), so the rules-of-hooks are not violated.
  return useContext(ChatbotStoreContext) ?? useChatbotStore;
}

/**
 * Subscribe to the active chatbot store via a selector — context store if present,
 * else the global singleton (byte-identical to `useChatbotStore(selector)`).
 */
export function useChatbotStoreContext<T>(selector: (state: ChatbotStore) => T): T {
  return useStore(useChatbotStoreApi(), selector);
}
